/**
 * Route path constants and path builder functions.
 * Centralizes all route paths to avoid string-literal duplication.
 */

export const paths = {
  // Public
  login: () => '/login',
  forgotPassword: () => '/forgot-password',
  resetPassword: () => '/reset-password',
  verifyEmail: (token) => `/verify-email/${token}`,

  // Error pages
  forbidden: () => '/403',
  serverError: () => '/500',
  maintenance: () => '/maintenance',

  // Protected — top level
  home: () => '/',
  users: () => '/users',
  builderUsers: () => '/builder-users',
  profile: () => '/profile',

  // Org
  org: (orgId) => `/orgs/${orgId}`,
  orgTab: (orgId, tab) => `/orgs/${orgId}/${tab}`,

  // Org modules (standalone routes)
  monitor: (orgId) => `/orgs/${orgId}/monitor`,
  monitorTab: (orgId, tab) => `/orgs/${orgId}/monitor/${tab}`,
  aiCost: (orgId) => `/orgs/${orgId}/ai-cost`,
  aiCostTab: (orgId, tab) => `/orgs/${orgId}/ai-cost/${tab}`,
  interfaces: (orgId) => `/orgs/${orgId}/interfaces`,
  interfacesTab: (orgId, tab) => `/orgs/${orgId}/interfaces/${tab}`,
  interfacesItem: (orgId, tab, itemId) => `/orgs/${orgId}/interfaces/${tab}/${itemId}`,
  configVariables: (orgId) => `/orgs/${orgId}/config-variables`,
  userManagement: (orgId) => `/orgs/${orgId}/user-management`,
  userManagementTab: (orgId, tab) => `/orgs/${orgId}/user-management/${tab}`,
  access: (orgId) => `/orgs/${orgId}/access`,
  orgStructure: (orgId) => `/orgs/${orgId}/org-structure`,
  context: (orgId) => `/orgs/${orgId}/context`,
  contextTab: (orgId, tab) => `/orgs/${orgId}/context/${tab}`,
  releases: (orgId) => `/orgs/${orgId}/releases`,
  requirements: (orgId) => `/orgs/${orgId}/requirements`,
  requirementsTab: (orgId, tab) => `/orgs/${orgId}/requirements/${tab}`,
  qa: (orgId) => `/orgs/${orgId}/qa`,
  qaTab: (orgId, tab) => `/orgs/${orgId}/qa/${tab}`,
  qaPackage: (orgId, packageId) => `/orgs/${orgId}/qa/packages/${packageId}`,
  requirementsModule: (orgId, packageId) => `/orgs/${orgId}/requirements/modules/${packageId}`,
  support: (orgId) => `/orgs/${orgId}/support`,
  supportApp: (orgId, supportAppId) => `/orgs/${orgId}/support/apps/${supportAppId}`,
  supportAppTab: (orgId, supportAppId, tab) => `/orgs/${orgId}/support/apps/${supportAppId}/${tab}`,
  llmConfig: () => '/llm-config',
  generalSettings: () => '/general-settings',
  pipelineGroup: (orgId, groupId) => `/orgs/${orgId}/pipelines/${groupId}`,
  pipelineDetail: (orgId, groupId, pipelineId) => `/orgs/${orgId}/pipelines/${groupId}/${pipelineId}`,
  agentGroup: (orgId, groupId) => `/orgs/${orgId}/agent-groups/${groupId}`,
  agentGroupAgent: (orgId, groupId, type, agentId) => `/orgs/${orgId}/agent-groups/${groupId}/${type}/${agentId}`,
  agentGroupInteractive: (orgId, groupId, agentId) => `/orgs/${orgId}/agent-groups/${groupId}/interactive/${agentId}`,
  agentGroupAgenticTool: (orgId, groupId, agentId) => `/orgs/${orgId}/agent-groups/${groupId}/agentic_tool/${agentId}`,
  agentGroupVoice: (orgId, groupId, agentId) => `/orgs/${orgId}/agent-groups/${groupId}/voice/${agentId}`,
  agentGroupRagService: (orgId, groupId) => `/orgs/${orgId}/agent-groups/${groupId}/rag_service`,
  agentBuilder: (orgId, agentId) => `/orgs/${orgId}/agents/${agentId}`,
  agentTest: (orgId, agentId) => `/orgs/${orgId}/agents/${agentId}/test`,
  aiEmployee: (orgId, employeeId) => `/orgs/${orgId}/ai-employees/${employeeId}`,
  aiPlayground: (orgId) => `/orgs/${orgId}/ai-playground`,
  knowledgeBase: (orgId, kbId) => `/orgs/${orgId}/knowledge-bases/${kbId}`,
  documents: (orgId) => `/orgs/${orgId}/documents`,
  importApp: (orgId) => `/orgs/${orgId}/import-app`,
  importPipelines: (orgId) => `/orgs/${orgId}/import-pipelines`,
  analyticsDashboard: (orgId, dashId) => `/orgs/${orgId}/analytics/${dashId}`,

  // App
  app: (orgId, appId) => `/orgs/${orgId}/apps/${appId}`,
  deploy: (orgId, appId) => `/orgs/${orgId}/apps/${appId}/deploy`,
  // Read-only diagram of the deployed appconfig. Unlike `deploy`, this one is
  // NOT image-tier — it renders on a consumer target, where it is the only way
  // to see what the running app is actually made of.
  deployedOverview: (orgId, appId) => `/orgs/${orgId}/apps/${appId}/deployed-overview`,

  // Editor (version-level views)
  editor: (orgId, appId, versionUid) => `/orgs/${orgId}/apps/${appId}/editor/${versionUid}`,
  editorView: (orgId, appId, versionUid, view) => `/orgs/${orgId}/apps/${appId}/editor/${versionUid}/${view}`,
  activities: (orgId, appId, versionUid) => `/orgs/${orgId}/apps/${appId}/editor/${versionUid}/activities`,
  activity: (orgId, appId, versionUid, activityId) => `/orgs/${orgId}/apps/${appId}/editor/${versionUid}/activities/${activityId}`,

  // App-level view/screen routes
  recordView: (orgId, appId, rvUid) => `/orgs/${orgId}/apps/${appId}/views/${rvUid}`,
  detailView: (orgId, appId, dvUid) => `/orgs/${orgId}/apps/${appId}/detail-views/${dvUid}`,
  activityView: (orgId, appId, avUid) => `/orgs/${orgId}/apps/${appId}/activity-views/${avUid}`,
  screen: (orgId, appId, screenUuid) => `/orgs/${orgId}/apps/${appId}/screens/${screenUuid}`,
  dvScreen: (orgId, appId, dvScreenUuid) => `/orgs/${orgId}/apps/${appId}/dv-screens/${dvScreenUuid}`,
  rvScreen: (orgId, appId, rvScreenUuid) => `/orgs/${orgId}/apps/${appId}/rv-screens/${rvScreenUuid}`,
  avScreen: (orgId, appId, avScreenUuid) => `/orgs/${orgId}/apps/${appId}/av-screens/${avScreenUuid}`,
  chartTemplate: (orgId, appId, ctUid) => `/orgs/${orgId}/apps/${appId}/chart-templates/${ctUid}`,
  chartScreen: (orgId, appId, chartScreenUuid) => `/orgs/${orgId}/apps/${appId}/chart-screens/${chartScreenUuid}`,
  mcScreen: (orgId, appId, mcScreenUuid) => `/orgs/${orgId}/apps/${appId}/mc-screens/${mcScreenUuid}`,
  headerConfig: (orgId, appId, deviceType) => `/orgs/${orgId}/apps/${appId}/header-config/${deviceType}`,
  formScreen: (orgId, appId, activityUid) => `/orgs/${orgId}/apps/${appId}/form-screens/${activityUid}`,
  reactComponent: (orgId, appId, rcUuid) => `/orgs/${orgId}/apps/${appId}/react-component/${rcUuid}`,
  rdbmsTemplate: (orgId, appId, templateUuid) => `/orgs/${orgId}/apps/${appId}/rdbms-templates/${templateUuid}`,
  rdbmsCrud: (orgId, appId, crudTemplateUuid) => `/orgs/${orgId}/apps/${appId}/rdbms-crud/${crudTemplateUuid}`,
  integration: (orgId, appId, integrationUuid) => `/orgs/${orgId}/apps/${appId}/integrations/${integrationUuid}`,
  dataset: (orgId, appId, datasetUuid) => `/orgs/${orgId}/apps/${appId}/datasets/${datasetUuid}`,
  appTemplate: (orgId, appId, templateUuid) => `/orgs/${orgId}/apps/${appId}/templates/${templateUuid}`,
  gitConfig: (orgId, appId) => `/orgs/${orgId}/apps/${appId}/git-config`,
  fileRepository: (orgId, appId) => `/orgs/${orgId}/apps/${appId}/file-repository`,
  linkedPipelines: (orgId, appId) => `/orgs/${orgId}/apps/${appId}/linked-pipelines`,
  appKnowledge: (orgId, appId) => `/orgs/${orgId}/apps/${appId}/app-knowledge`,

  // Themes (global)
  themes: () => '/themes',
  themeEditor: (themeId) => `/themes/${themeId}`,

  // Custom Domains (global, platform-admin)
  customDomains: () => '/custom-domains',
};

/**
 * Maps AppDashboard tab IDs to route segments.
 * Used by Header module switcher and tab bar.
 */
export const TAB_ROUTE_MAP = {
  apps: 'apps',
  pipelines: 'pipelines-list',
  agents: 'agents-list',
  analytics: 'analytics-list',
  'ai-employees': 'ai-employees',
  'user-management': 'access',
};

/**
 * Reverse map: route segment → tab ID for highlighting active tab.
 */
export const ROUTE_TO_TAB_MAP = Object.fromEntries(
  Object.entries(TAB_ROUTE_MAP).map(([k, v]) => [v, k])
);
